import { CheckOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import cn from "../../../../../utilities/tailwind-merge";

/**
 * Step indicator for the sell-insurance wizard. Desktop shows every step by name, and
 * completed steps can be clicked to go back. Phones get a compact segmented bar plus the
 * current step's name, so the indicator never pushes the form below the fold.
 */
const WizardProgress = ({ steps, current, onStepClick, disabled }) => {
    const { t } = useTranslation("sellInsurance");
    const total = steps.length;

    return (
        <div>
            <div className="lg:hidden" aria-live="polite">
                <div className="flex items-baseline justify-between gap-3">
                    <p className="mb-0 text-base font-semibold text-[#2F2F46]">{steps[current]?.title}</p>
                    <p className="mb-0 shrink-0 text-sm font-medium text-gray-500">
                        {t("wizard.stepProgress", { current: current + 1, total })}
                    </p>
                </div>
                <div
                    className="mt-2 grid gap-1.5"
                    style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={total}
                    aria-valuenow={current + 1}>
                    {steps.map((step, index) => (
                        <span
                            key={step.title}
                            className={cn(
                                "h-1.5 rounded-full transition-colors",
                                index <= current ? "bg-[#5D5DF6]" : "bg-gray-200",
                            )}
                        />
                    ))}
                </div>
            </div>

            <ol className="mb-0 hidden list-none items-center gap-2 p-0 lg:flex">
                {steps.map((step, index) => {
                    const isDone = index < current;
                    const isCurrent = index === current;
                    const canGoBack = isDone && !disabled;

                    return (
                        <li key={step.title} className="flex min-w-0 flex-1 items-center gap-2 last:flex-none">
                            <button
                                type="button"
                                onClick={canGoBack ? () => onStepClick(index) : undefined}
                                disabled={!canGoBack}
                                aria-current={isCurrent ? "step" : undefined}
                                className={cn(
                                    "flex shrink-0 items-center gap-2 rounded-full py-1 pl-1 pr-3 text-base font-medium transition-colors",
                                    isCurrent && "bg-[#F0F0FE] text-[#5D5DF6]",
                                    isDone && "text-[#2F2F46]",
                                    canGoBack && "cursor-pointer hover:bg-gray-100",
                                    !isDone && !isCurrent && "text-gray-400",
                                    !canGoBack && "cursor-default",
                                )}>
                                <span
                                    className={cn(
                                        "flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold",
                                        isCurrent && "bg-[#5D5DF6] text-white",
                                        isDone && "bg-[#5D5DF6]/10 text-[#5D5DF6]",
                                        !isDone && !isCurrent && "bg-gray-100 text-gray-500",
                                    )}>
                                    {isDone ? <CheckOutlined className="text-[11px]" /> : index + 1}
                                </span>
                                {step.title}
                            </button>
                            {index < total - 1 && (
                                <span
                                    className={cn(
                                        "h-px min-w-4 flex-1",
                                        index < current ? "bg-[#5D5DF6]/40" : "bg-gray-200",
                                    )}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

WizardProgress.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({ title: PropTypes.string.isRequired })).isRequired,
    current: PropTypes.number.isRequired,
    onStepClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

WizardProgress.defaultProps = {
    disabled: false,
};

export default WizardProgress;
